import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Download, LogOut, Search, BarChart3 } from "lucide-react";
import { toast } from "sonner";

const ADMIN_KEY = "shinrai-admin-password";

function LoginForm({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { password });
      sessionStorage.setItem(ADMIN_KEY, password);
      onSuccess(password);
    } catch {
      toast.error("Mot de passe invalide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-pearl px-6" data-testid="admin-login">
      <form onSubmit={handleSubmit} className="w-full max-w-md bg-white border border-ink/10 p-10">
        <div className="font-mono-overline text-mute mb-3">Administration</div>
        <h1 className="font-serif text-3xl mb-2">Espace privé ShinRai</h1>
        <p className="text-sm text-mute mb-8">Accès réservé à l'administrateur du site.</p>
        <label className="font-mono-overline text-mute mb-2 block">Mot de passe</label>
        <input
          type="password"
          className="shin-input mb-6"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoFocus
          data-testid="admin-password-input"
        />
        <button type="submit" className="btn-primary w-full justify-center" disabled={loading} data-testid="admin-login-submit">
          {loading ? "Connexion…" : "Se connecter"}
        </button>
      </form>
    </main>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white border border-ink/10 p-6" data-testid={testid}>
      <div className="font-mono-overline text-mute mb-2">{label}</div>
      <div className="font-serif text-4xl">{value ?? "—"}</div>
    </div>
  );
}

export default function AdminPage() {
  useDocumentTitle("Administration");
  const [password, setPassword] = useState(() => sessionStorage.getItem(ADMIN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const headers = { "X-Admin-Password": password };

  useEffect(() => {
    if (!password) return;
    setLoading(true);
    Promise.all([
      api.get("/admin/leads", { headers }).then((r) => setLeads(r.data)),
      api.get("/admin/stats", { headers }).then((r) => setStats(r.data)),
    ])
      .then(() => setAuthed(true))
      .catch(() => {
        sessionStorage.removeItem(ADMIN_KEY);
        setPassword("");
        toast.error("Session expirée. Reconnecte-toi.");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password]);

  if (!authed) {
    return <LoginForm onSuccess={(p) => setPassword(p)} />;
  }

  const logout = () => {
    sessionStorage.removeItem(ADMIN_KEY);
    setPassword("");
    setAuthed(false);
  };

  const exportCsv = () => {
    const url = `${API}/admin/leads/export`;
    // Use fetch + blob to attach the header
    fetch(url, { headers })
      .then((r) => {
        if (!r.ok) throw new Error("export failed");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "shinrai-leads.csv";
        a.click();
      })
      .catch(() => toast.error("Échec de l'export"));
  };

  const visibleLeads = leads.filter((l) => {
    if (sourceFilter !== "all") {
      if (sourceFilter === "diagnostic_form" && l.source !== "diagnostic_form") return false;
      if (sourceFilter === "free_download" && !l.source?.startsWith("free_download")) return false;
    }
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return [l.first_name, l.last_name, l.email, l.company, l.source].some((v) =>
      (v || "").toLowerCase().includes(q)
    );
  });

  return (
    <main className="min-h-screen pt-28 pb-24 bg-pearl" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
          <div>
            <div className="font-mono-overline text-mute mb-2">Administration</div>
            <h1 className="font-serif text-5xl leading-tight" data-testid="admin-title">Pipeline ShinRai</h1>
          </div>
          <button onClick={logout} className="btn-ghost text-sm" data-testid="admin-logout">
            <LogOut size={14} /> Se déconnecter
          </button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          <Stat label="Total leads" value={stats?.leads_total} testid="stat-total" />
          <Stat label="Diagnostic" value={stats?.leads_diagnostic} testid="stat-diag" />
          <Stat label="Téléchargements" value={stats?.leads_free_downloads} testid="stat-dl" />
          <Stat label="Transactions payées" value={stats?.transactions_paid} testid="stat-paid" />
        </div>

        <div className="bg-white border border-ink/10">
          <div className="flex flex-wrap items-center justify-between gap-3 p-5 border-b border-ink/10">
            <div className="flex items-center gap-3 flex-1 min-w-[240px]">
              <div className="relative flex-1 max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute" />
                <input
                  className="shin-input pl-9"
                  placeholder="Rechercher (nom, email, société, source…)"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  data-testid="admin-search"
                />
              </div>
              <select
                className="shin-input max-w-[200px]"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                data-testid="admin-source-filter"
              >
                <option value="all">Toutes les sources</option>
                <option value="diagnostic_form">Diagnostic</option>
                <option value="free_download">Téléchargements gratuits</option>
              </select>
            </div>
            <button onClick={exportCsv} className="btn-primary text-sm" data-testid="admin-export-csv">
              <Download size={14} /> Exporter CSV
            </button>
          </div>

          {loading ? (
            <div className="p-10 text-center text-mute">Chargement…</div>
          ) : visibleLeads.length === 0 ? (
            <div className="p-10 text-center text-mute">Aucun lead à afficher.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-leads-table">
                <thead>
                  <tr className="text-left text-mute font-mono-overline border-b border-ink/10">
                    <th className="p-4">Date</th>
                    <th className="p-4">Nom</th>
                    <th className="p-4">Société</th>
                    <th className="p-4">Contact</th>
                    <th className="p-4">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLeads.map((l) => {
                    const d = new Date(l.created_at);
                    const dateLabel = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
                    const timeLabel = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
                    return (
                      <tr key={l.id} className="border-b border-ink/5 hover:bg-pearl-dark/40 transition">
                        <td className="p-4 align-top">
                          <div className="text-ink">{dateLabel}</div>
                          <div className="text-xs text-mute">{timeLabel}</div>
                        </td>
                        <td className="p-4 align-top">
                          <div className="text-ink font-medium">{l.first_name} {l.last_name}</div>
                          {l.challenge && <div className="text-xs text-mute mt-1">{l.challenge}</div>}
                          {l.team_size && <div className="text-xs text-mute">{l.team_size} collab.</div>}
                        </td>
                        <td className="p-4 align-top text-ink">{l.company || "—"}</td>
                        <td className="p-4 align-top">
                          <a href={`mailto:${l.email}`} className="text-ink hover:underline">{l.email}</a>
                          <div className="text-xs text-mute mt-1">{l.phone}</div>
                        </td>
                        <td className="p-4 align-top">
                          <span className="inline-block px-2 py-1 bg-pearl-dark text-xs text-ink font-medium">
                            {l.source}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
